import { useState, useRef, useCallback } from 'react'
import { OrderHttpRepository } from 'orders/infrastructure/repositories/OrderHttpRepository'
import { ListOrdersUseCase } from 'orders/usecases/ListOrdersUseCase'
import { OrderFilters } from 'orders/domain/repositories/IOrderRepository'
import { OrderAdapter, OrderListItemView } from 'orders/infrastructure/adapters/OrderAdapter'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../store/store'
import { setState } from '../../../store/slices/order/orderSlice'

interface UsePaginationState {
    totalItems: number
    page: number
    itemsPerPage: number
}

export function useOrderList() {
    const [items, setItems] = useState<OrderListItemView[]>([])
    const [pagination, setPagination] = useState<UsePaginationState>({
        totalItems: 0,
        page: 1,
        itemsPerPage: 10,
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const dispatch = useDispatch<AppDispatch>()

    const repositoryRef = useRef(new OrderHttpRepository())
    const useCaseRef = useRef(new ListOrdersUseCase(repositoryRef.current))
    const requestIdRef = useRef(0)
    const lastFiltersRef = useRef<string>('')
    const currentFiltersRef = useRef<OrderFilters | null>(null)

    const fetchOrders = useCallback(async (filters: OrderFilters) => {
        const key = JSON.stringify(filters)
        if (key === lastFiltersRef.current) return
        lastFiltersRef.current = key
        currentFiltersRef.current = filters

        const thisId = ++requestIdRef.current
        setLoading(true)
        setError(null)

        try {
            const result = await useCaseRef.current.execute(filters)

            if (thisId !== requestIdRef.current) return

            const viewItems = OrderAdapter.toViewList(result.items)
            setItems(viewItems)
            setPagination({
                totalItems: result.totalItems,
                page: result.page,
                itemsPerPage: result.size,
            })
            dispatch(setState({
                totalItems: result.totalItems,
                items: viewItems,
                page: result.page,
                size: result.size,
                loading: false,
            }))
        } catch (err) {
            if (thisId === requestIdRef.current) {
                setError('Error al cargar las órdenes')
                console.error('[useOrderList] Error fetching orders:', err)
            }
        } finally {
            if (thisId === requestIdRef.current) {
                setLoading(false)
            }
        }
    }, [dispatch])

    const changePage = useCallback((page: number, currentFilters: OrderFilters) => {
        setPagination((prev) => ({ ...prev, page }))
        lastFiltersRef.current = ''
        fetchOrders({ ...currentFilters, page })
    }, [fetchOrders])

    const updateFilters = useCallback((filters: OrderFilters) => {
        setPagination((prev) => ({ ...prev, page: 1 }))
        lastFiltersRef.current = ''
        fetchOrders({ ...filters, page: 1 })
    }, [fetchOrders])

    const refetch = useCallback(() => {
        if (!currentFiltersRef.current) return
        lastFiltersRef.current = ''
        return fetchOrders(currentFiltersRef.current)
    }, [fetchOrders])

    return {
        items,
        pagination,
        loading,
        error,
        fetchOrders,
        changePage,
        updateFilters,
        refetch,
    }
}
